import { ticketServiceApi } from "../../../../app/superAdmin/ticketServiceApi";
import { API_URL } from "../../../../constants/apiUrl";
import { GetQueryArgsType } from "../../../../types";
import {
  AllTicketsParams,
  AllTicketsResponse,
  AssignedToMeTicketsParams,
  AssignToMeTicketsResponse,
  CommentHistoryResponse,
  TicketDetailsHistoryParams,
  TicketDetailsHistoryResponse,
  TicketDetailsResponse,
} from "./ticket.type";

export const ticketApi = ticketServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get Queries
    getAllTickets: builder.query<
      AllTicketsResponse,
      GetQueryArgsType<AllTicketsParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getAllTicketLogs,
        ...queryArgs,
      }),
      providesTags: ["GetAllTicketLogs"],
    }),

    getAssignedToMeTickets: builder.query<
      AssignToMeTicketsResponse,
      GetQueryArgsType<AssignedToMeTicketsParams>
    >({
      query: ({ params }) => {
        const { id, ...rest } = params || {};
        return {
          url: `${API_URL.getApprovalHubTickets}/${id}`,
          params: { ...rest },
        };
      },
      providesTags: ["GetApprovalHubTickets"],
    }),

    getTicketDetails: builder.query<TicketDetailsResponse, any>({
      query: (id) => ({
        url: `${API_URL.getTicketDetails}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetTicketDetails", id }],
    }),

    getTicketLogDetails: builder.query<
      TicketDetailsResponse,
      GetQueryArgsType<{ id: string; emp_id: string }>
    >({
      query: ({ params }) => ({
        url: `${API_URL.getTicketLogDetails}/${params?.id}`,
        params: {
          emp_id: params?.emp_id,
        },
      }),
      providesTags: ["GetAllMyTicketDetails"],
    }),

    getTicketDetailsHistory: builder.query<
      TicketDetailsHistoryResponse,
      GetQueryArgsType<TicketDetailsHistoryParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getTicketIDRelatedNotifications,
        ...queryArgs,
      }),
      providesTags: ["GetTicketIDRelatedNotifications"],
    }),

    getCommentHistory: builder.query<CommentHistoryResponse, any>({
      query: (id) => ({
        url: `${API_URL.getCommentHistory}/${id}`,
      }),
      providesTags: ["GetCommentHistory"],
    }),

    // Mutations
    createTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.raiseTicket}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetRaiseTickets", "GetAllTicketLogs"];
        return [];
      },
    }),

    updateTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.updateRaisedTicket}/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result)
          return [
            "GetRaiseTickets",
            "GetAllTicketLogs",
            { type: "GetTicketDetails", id: result.id },
          ];
        return [];
      },
    }),

    reopenTicket: builder.mutation({
      query: ({ id }) => ({
        url: `${API_URL.reopenTicket}/${id}`,
        method: "PATCH",
      }),
      invalidatesTags: (_, __, id) => {
        return [
          "GetRaiseTickets",
          "GetAllTicketLogs",
          "GetAllMyTickets",
          "GetAllMyTicketDetails",
          "GetTicketIDRelatedNotifications",
          { type: "GetTicketDetails", id },
        ];
      },
    }),

    approveTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.approveRaiseTicket}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "GetAllMyTicketDetails",
        "GetApprovalHubTickets",
        "GetTicketIDRelatedNotifications",
      ],
    }),

    rejectTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.rejectRaiseTicket}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "GetAllMyTicketDetails",
        "GetApprovalHubTickets",
        "GetTicketIDRelatedNotifications",
      ],
    }),

    resolveTicket: builder.mutation({
      query: ({ id }) => ({
        url: `${API_URL.resolveOpenTicket}/${id}`,
        method: "PATCH",
      }),
      invalidatesTags: ["GetAllMyTicketDetails", "GetAllMyTickets"],
    }),

    cancelTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.cancelTicket}/${id}/cancel`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "GetAllMyTickets",
        "GetAllMyTicketDetails",
        "GetTicketIDRelatedNotifications",
      ],
    }),

    closeTicket: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.closeTicket}/${id}/status/completed`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "GetAllMyTickets",
        "GetAllMyTicketDetails",
        "GetTicketIDRelatedNotifications",
      ],
    }),

    shiftTemplatePriority: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.shiftPriority}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "GetAllTickets",
        "GetAllTicketLogs",
        "GetAllMyTickets",
        "GetApprovalHubTickets",
        "GetTicketForCC",
      ],
    }),

    forwardTicketToDepartment: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.updateForwardTicket}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [
        "GetAllMyTicketDetails",
        "GetAllMyTickets",
        "GetTicketIDRelatedNotifications",
      ],
    }),

    forwardTicketToEmployee: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.forwardOpenTicket}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GetAllMyTicketDetails", "GetAllMyTickets"],
    }),

    addTicketCC: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.addCc}?employeeId=${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_, __, { ticket_log_id }) => [
        "GetAllMyTicketDetails",
        "GetAllMyTickets",
        "GetAllCategories",
        "GetAllTicketLogs",
        "GetRaiseTickets",
        "GetTicketIDRelatedNotifications",
        { type: "GetTicketDetails", id: ticket_log_id },
        { type: "GetCCList", id: ticket_log_id },
      ],
    }),

    createComment: builder.mutation({
      query: (data) => ({
        url: API_URL.addComment,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_, __, { ticketLogId }) => [
        "GetAllMyTicketDetails",
        "GetAllMyTickets",
        "GetAllCategories",
        "GetAllTicketLogs",
        "GetRaiseTickets",
        "GetCommentHistory",
        { type: "GetCommentHistory", id: ticketLogId },
        { type: "GetTicketDetails", id: ticketLogId },
        { type: "GetCCList", id: ticketLogId },
      ],
    }),

    updateComment: builder.mutation({
      query: ({ id, data }) => ({
        url: `${API_URL.editComment}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_, __, { data }) => [
        "GetAllMyTicketDetails",
        "GetAllMyTickets",
        "GetAllCategories",
        "GetAllTicketLogs",
        "GetRaiseTickets",
        "GetCommentHistory",
        { type: "GetCommentHistory", id: data?.ticketLogId },
        { type: "GetTicketDetails", id: data?.ticketLogId },
        { type: "GetCCList", id: data?.ticketLogId },
      ],
    }),
  }),
});

export const {
  // Queries
  useGetAllTicketsQuery,
  useGetAssignedToMeTicketsQuery,
  useGetTicketDetailsQuery,
  useGetTicketLogDetailsQuery,
  useGetTicketDetailsHistoryQuery,
  useGetCommentHistoryQuery,

  // Mutations
  useCreateTicketMutation,
  useUpdateTicketMutation,
  useReopenTicketMutation,
  useApproveTicketMutation,
  useRejectTicketMutation,
  useResolveTicketMutation,
  useCancelTicketMutation,
  useCloseTicketMutation,
  useShiftTemplatePriorityMutation,
  useForwardTicketToDepartmentMutation,
  useForwardTicketToEmployeeMutation,
  useAddTicketCCMutation,
  useCreateCommentMutation,
  useUpdateCommentMutation,
} = ticketApi;
